/**
 * The day in words that more than one screen says: a break with a time to be back, what Resume
 * will do, why rounds are waiting, and the admin's message while everything is paused. Pure, so
 * the wall, the tablets, the phones and the admin screen can never disagree.
 */

import { pastLastWhistle } from "../engine/autopilot";
import { currentRound, heldForScores, startsByItselfAt } from "../engine/selectors";
import { NOTICE_MS } from "../engine/state";
import type { TournamentState } from "../engine/types";

const CLOCK = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

/** The time of day in the hall, "15:10". */
export function hhmm(ms: number): string {
  return CLOCK.format(ms);
}

/** "12:05" from milliseconds, never below zero. */
export function countdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
}

/** What a break is called when the admin gave a time and no words. */
export const BREAK_NAME = "Break";
/** What every screen says once the time to be back has passed. */
export const BREAK_OVER = "Back on court now";

export interface Break {
  /** "Lunch". */
  label: string;
  /** "Back at 15:10", or null when no time was given. */
  backAt: string | null;
  /** Time until then; null when no time was given. */
  leftMs: number | null;
  /** True once that time has passed. Nothing starts by itself: the admin still has to resume. */
  over: boolean;
}

/** The break that is on, or null: not paused, or a plain pause with no time and no words. */
export function breakNow(state: TournamentState, now: number): Break | null {
  if (!state.pausedAll) return null;
  const { pausedUntil, pauseLabel } = state;
  if (pausedUntil === null && pauseLabel === null) return null;
  return {
    label: pauseLabel ?? BREAK_NAME,
    backAt: pausedUntil === null ? null : `Back at ${hhmm(pausedUntil)}`,
    leftMs: pausedUntil === null ? null : Math.max(0, pausedUntil - now),
    over: pausedUntil !== null && now >= pausedUntil,
  };
}

export type Waiting =
  /** "Start rounds by themselves" is off. */
  | { why: "switch" }
  /** Nobody entered a score for round `roundNo`, so the next one is held. */
  | { why: "scores"; roundNo: number }
  /** A late score let that hold go: round `roundNo` starts by itself at `startsAt`. */
  | { why: "soon"; roundNo: number; startsAt: number }
  | null;

/**
 * Why the next round has not started by itself, or null when there is nothing to say. The switch
 * comes first: with it off, a score would not start anything either. Past the last whistle it is
 * always null: no round is coming, so no screen should promise one.
 */
export function waitingFor(state: TournamentState, now: number): Waiting {
  if (pastLastWhistle(state, now)) return null;
  if (!state.config.autoAdvance) return { why: "switch" };
  const round = currentRound(state);
  if (!round || state.clock.phase !== "idle") return null;
  if (heldForScores(state)) return { why: "scores", roundNo: round.no };
  const startsAt = startsByItselfAt(state);
  if (startsAt !== null && now < startsAt && !state.pausedAll && !pastLastWhistle(state, startsAt)) {
    return { why: "soon", roundNo: round.no + 1, startsAt };
  }
  return null;
}

/** Under the Resume button: what tapping it will do. */
export function resumeLine(state: TournamentState, now: number): string {
  const { phase, phaseEndsAt, pausedAt, roundNo } = state.clock;
  const left = phaseEndsAt === null ? 0 : Math.max(0, phaseEndsAt - (pausedAt ?? now));
  const nextNo = state.rounds.length + 1;
  const nothing = "Resume: nothing starts until you tap Start.";
  if (phase === "playing") return `Resume: round ${roundNo ?? state.rounds.length} carries on with ${countdown(left)} left.`;
  // between rounds after a late score: the start it set going does not move for a pause
  const wait = phase === "idle" ? Math.max(0, (startsByItselfAt(state) ?? 0) - now) : left;
  // as the autopilot decides it: the switch, the empty-hall hold, the last whistle
  const byItself = state.config.autoAdvance && state.rounds.length > 0 && !heldForScores(state) && !pastLastWhistle(state, now + wait);
  if (!byItself) return nothing;
  if (phase === "changeover" || wait > 0) return `Resume: round ${nextNo} starts in ${countdown(wait)}.`;
  return `Resume: round ${nextNo} starts at once.`;
}

export interface WallNotice {
  text: string;
  at: number;
  /** True while it is held on the pause screen: no 20 seconds, it stays until the pause ends. */
  untilResume: boolean;
}

/**
 * The admin's message as the wall shows it right now, or null. Normally 20 seconds from when it
 * was sent. While everything is paused (and no podium or awards are up) a message sent during
 * the pause, or still up when the pause began, stays until the pause ends or it is taken down.
 */
export function wallNotice(state: TournamentState, now: number): WallNotice | null {
  const notice = state.notice;
  if (!notice) return null;
  const revealing = state.reveal.main || state.reveal.fun || state.reveal.awards;
  if (state.pausedAll && !revealing) {
    const pausedAt = state.clock.pausedAt;
    if (pausedAt === null || notice.at < pausedAt - NOTICE_MS) return null;
    return { text: notice.text, at: notice.at, untilResume: true };
  }
  return now - notice.at < NOTICE_MS ? { text: notice.text, at: notice.at, untilResume: false } : null;
}
